import { View } from 'backbone';
import { Consola } from 'consola';
import $ from 'jquery';
import _ from 'underscore';
import VideoHtml from '../html/engine/videoScreen.html';
import alerts from './alerts/alerts';
import { Events as DecoratorEvents, Decorators } from './decorators';
import BaseDecorator from './decorators/baseDecorator';
import { getLogger } from './logging/logger';
import AudioManager from './manager/audioManager';
import gsap from './util/hwpTVGSAP';
import {
	arrayPromiseChain,
	getVideoErrorMessage,
	promisify,
	pruneObject,
	wait,
} from './util/utilities';

/**
 * @typedef {import('./engine/arcade/arcade.types').PageOptions} PageOptions
 * @typedef {import('./decorators').DecoratorElement} DecoratorElement
 *
 * @typedef {object} VideoData
 * @property {string} video
 * @property {string} [videoImage]
 * @property {string} [videoCaption]
 *
 * @typedef {Backbone.ViewOptions<undefined, HTMLElement> & PageOptions} BaseViewOptions
 */

/**
 * Checks if a given GSAP target is valid.
 *
 * @param {any} target - The target to check.
 * @returns {boolean} - True if the target is valid, false otherwise.
 */
function isValidGsapTarget(target) {
	return !!target && Array.isArray(target) ? target.some((item) => !!item) : true;
}

/**
 * @class
 * @extends {View}
 * @template {BaseViewOptions}[ViewOptions=BaseViewOptions]
 */
export default class BaseView extends View {
	get CN() {
		return BaseView.name;
	}

	/**
	 * Returns a logger instance scoped to the current class.
	 * @returns {Consola} The logger instance.
	 */
	get logger() {
		return getLogger(this.CN);
	}

	/**
	 * Returns the element ID attribute.
	 * @override
	 * @type {() => string | string}
	 */
	id() {
		return '';
	}

	/**
	 * Returns the element class name attribute.
	 * @type {() => string | string}
	 */
	className() {
		return '';
	}

	/**
	 * Returns the element tag name attribute.
	 * @returns {string} The element tag name attribute.
	 */
	tagName() {
		return 'div';
	}

	/**
	 * Returns an object containing event handlers for the view.
	 * @type {() => import('backbone').EventsHash} An object mapping event names to event handler functions.
	 */
	events() {
		return {};
	}

	/**
	 * Returns a compiled Underscore.js template for the view.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get template() {
		return _.template('');
	}

	/**
	 * Returns a compiled Underscore.js template for the video screen.
	 * @returns {_.CompiledTemplate} The compiled video template.
	 */
	get videoTemplate() {
		return _.template(VideoHtml);
	}

	/**
	 * Constructs a new instance of the BaseView class with the provided options.
	 *
	 * @param {ViewOptions} [options] - The options to initialize the BaseView instance with.
	 */
	constructor(options) {
		super(options);

		this.logger.log('[constructor]', { options });
	}

	/**
	 * @param {ViewOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]<${BaseView.name}>`, { options });

		/** @type {ViewOptions} */
		this.options = options || {};
		Object.assign(this, options);
	}

	/**
	 * Renders the view by applying the template to the view's element and appending it to the DOM.
	 *
	 * @returns {Promise<any>} The current instance of the view for chaining.
	 */
	render() {
		this.logger.log(`[${this.render.name}]<${BaseView.name}>`);

		if (this.template) {
			this.$el.empty().append(this.template({ ...this.options }));
		}

		return Promise.resolve(this);
	}

	/**
	 * Dismisses a DOM node. If a string is provided as an element selector, the lookup of that
	 * selector will be limited to the views DOM tree.
	 *
	 * @param {string|HTMLElement|HTMLElement[]|JQuery} target Some reference to the element to dismiss.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {Promise<void>}
	 */
	dismissElement(target, props) {
		this.logger.log(`[${this.dismissElement.name}]`, { target, props });

		if (typeof target === 'string') {
			target = this.$(target).toArray();
		}

		if (!isValidGsapTarget(target)) {
			this.logger.warn('Invalid GSAP targets');
			return Promise.resolve();
		}

		return gsap
			.timeline()
			.fadeOut(target, pruneObject({ ...props }))
			.then(() => Promise.resolve());
	}

	/**
	 * Displays a DOM node. If a string is provided as an element selector, the lookup of that
	 * selector will be limited to the views DOM tree.
	 * @param {string|HTMLElement|HTMLElement[]|JQuery} target Some reference to the element to display.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {Promise<void>}
	 */
	displayElement(target, props) {
		this.logger.log(`[${this.displayElement.name}]`, { target, props });

		if (typeof target === 'string') {
			target = this.$(target).toArray();
		}

		if (!isValidGsapTarget(target)) {
			this.logger.warn('Invalid GSAP targets');
			return Promise.resolve();
		}

		return gsap
			.timeline()
			.fadeIn(target, pruneObject({ ...props }))
			.then(() => Promise.resolve());
	}

	/**
	 * Replaces a DOM node's descendent tree with another one by dismissing the target, replacing
	 * it's children, and displaying it again. If a string is provided as an element selector, the
	 * lookup of that selector will be limited to the views DOM tree.
	 * @param {string | HTMLElement | JQuery} target Some reference to the element to dismiss.
	 * @param {string | HTMLElement | JQuery} view The view to be inserted into the DOM to replace the
	 * targets chidlren.
	 * @param {function} [onReplaced] A callback function that will be called after the DOM has been
	 * updated, but before the elements are displayed.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {Promise<any>}
	 */
	replaceElement(target, view, onReplaced, props) {
		this.logger.log(`[${this.replaceElement.name}]`, { target, view, props });

		const $target = _.isString(target) ? this.$(target) : $(target);

		return this.dismissElement(target, props)
			.then(() => {
				const videos = $target.find('video').toArray();
				if (videos.length) {
					// Clean up any videos
					videos.forEach(this.cleanUpVideo.bind(this));
				}

				$target.empty().append(view);
			})
			.then(() => onReplaced?.())
			.then(() => {
				if (typeof view === 'string' && !/<\/\w+>/.test(view)) {
					// Doesn't appear to be html string, so prevent attaching decorators
					this.logger.debug('Skipping decorators for non-html string view');
					return Promise.resolve();
				}

				return this.attachDecorators();
			})
			.then(() => this.displayElement(target, props));
	}

	/**
	 * Dismisses the current views DOM node.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {GSAPTimeline}
	 */
	dismissView(props) {
		this.logger.log(`[${this.dismissView.name}]`, { props });

		return this.dismissElement(this.el, props);
	}

	/**
	 * Displays the current views DOM node.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {GSAPTimeline}
	 */
	displayView(props) {
		this.logger.log(`[${this.displayView.name}]`, { props });

		return this.displayElement(this.el, props);
	}

	/**
	 * Replaces the current views DOM node descendent tree with another one by dismissing the target,
	 * replacing it's children, and displaying it again.
	 * @param {string | HTMLElement | JQuery} view The view to be inserted into the DOM to replace the
	 * targets chidlren.
	 * @param {function} [onReplaced] A callback function that will be called after the DOM has been
	 * updated, but before the elements are displayed.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * both dismiss and display durations.
	 * @returns {Promise<any>}
	 */
	replaceView(view, onReplaced, props) {
		this.logger.log(`[${this.replaceView.name}]`, { view, onReplaced, props });

		return this.replaceElement(this.el, view, onReplaced, props);
	}

	/**
	 * Displays a video element in the specified container, optionally allowing controls to be displayed.
	 *
	 * @param {VideoData} videoData - An object containing video data, including the video URL.
	 * @param {string | HTMLElement | JQuery<HTMLElement>} [container=this.el] - The container element to display the video in.
	 * @param {boolean} [showControls=false] - Whether to display the video controls.
	 * @param {() => Promise<any> | void} [onDisplayed] - A callback function to be called after the video has been displayed.
	 * @returns {Promise<void>} - A promise that resolves when the video has finished playing.
	 */
	displayVideo(videoData, container = this.el, showControls = false, onDisplayed = undefined) {
		this.logger.log(`[${this.displayVideo.name}]`, { videoData, container, showControls });

		if (!videoData.video) {
			return Promise.reject(new Error('No video URL provided.'));
		}

		const videoScreen = this.videoTemplate(videoData);
		return this.replaceElement(container, videoScreen, () => {
			const $video = /** @type {JQuery<HTMLVideoElement>} */ (this.$('video'));
			const video = $video.get(0);
			if (video) {
				$video.one('canplaythrough', () => {
					this.logger.debug('[video][canplaythrough]');
					AudioManager.stopAllSounds();
					this.playVideo(video, showControls);
				});
				$video.on('error', () => {
					const { currentSrc } = video || {};
					const error = getVideoErrorMessage(video);
					alerts.danger(`[Video Error] [${error}] ["${currentSrc}"]`);
					this.logger.error('[Video Error]', { error, currentSrc });
				});

				video.load(); // Required for iOS Safari
			} else {
				this.logger.error('[Video not found in DOM]');
			}
		})
			.then(() => onDisplayed?.())
			.then(() => new Promise((resolve) => this.$('video').one('ended', () => resolve())));
	}

	/**
	 * Plays a video element, optionally allowing controls to be displayed.
	 *
	 * @param {HTMLVideoElement} video - The video element to play.
	 * @param {boolean} [allowControls = false] - Whether to display the video controls.
	 *
	 * @returns {Promise<void>} - A promise that resolves when the video has finished playing.
	 */
	playVideo(video, allowControls = false) {
		this.logger.log(`[${this.playVideo.name}]`, { video, allowControls });

		if (!video) {
			this.logger.error('Invalid Video');
			return Promise.reject(new Error('Invalid Video'));
		}

		const $vid = $(video);
		/** @type {NodeJS.Timeout} */
		let controlsTimeout;

		if (allowControls) {
			video.setAttribute('controls', '');
		} else {
			// Create timeout to activate controls
			controlsTimeout = setTimeout(() => {
				if (video.paused) {
					/** Video is still paused. This could be a situation that needs the controls activated to start playback. */
					video.setAttribute('controls', '');
				}
			}, 500);

			// listen for event to clear the controls timeout
			$vid.one('play', () => {
				/** Video playback succesful. Don't worry about activating controls to start playback.  */
				video.removeAttribute('controls');
				clearTimeout(controlsTimeout);
			});
		}

		// Prep audio pause and resume
		AudioManager.pauseBackgroundMusic();
		$vid.on('ended', () => AudioManager.resumeBackgroundMusic());

		// play video
		return video.play();
	}

	/**
	 * Cleans up a video element by pausing it, clearing its source, and reloading it. If no video
	 * element is provided, all video elements in the view are cleaned up.
	 *
	 * @param {string|HTMLVideoElement} [video='video'] - The video element to clean up, either as
	 * a jQuery selector string or a HTMLVideoElement object.
	 */
	cleanUpVideo(video = 'video') {
		this.logger.log(`[${this.cleanUpVideo.name}]`, { video });

		const $vid = _.isString(video) ? this.$(video) : $(video);

		$vid.toArray().forEach((vid) => {
			if (vid instanceof HTMLVideoElement) {
				vid.pause();
				vid.src = '';
				vid.load();
			}
		});
	}

	/**
	 * Adds decorators to the view's elements based on the Decorators configuration.
	 * It initializes each decorator and waits for them to be updated recursively,
	 * with ancestors updating before their children.
	 *
	 * @param {JQuery | HTMLElement | string} [targetElement] - The element to attach decorators to.
	 * @returns {Promise<BaseDecorator[]>} A promise that resolves when the decorators have been attached and updated.
	 */
	attachDecorators(targetElement) {
		this.logger.log(`[${this.attachDecorators.name}]`, { targetElement });

		const rootElement = targetElement ? $(targetElement) : this.$el;

		/**
		 * Recursively collect elements with decorators, grouping by depth level
		 * @param {JQuery} element
		 * @param {number} depth
		 * @returns {Map<number, DecoratorElement[]>}
		 */
		const collectElementsByDepth = (element, depth = 0) => {
			const elementsByDepth = new Map();

			Decorators.forEach(({ decorator: Decorator, selector }) => {
				const matchingElements = element.find(selector).toArray();

				matchingElements.forEach((/** @type {DecoratorElement} */ el) => {
					const $el = $(el);
					const elementDepth = depth + $el.parentsUntil(rootElement).length;

					if (!elementsByDepth.has(elementDepth)) {
						elementsByDepth.set(elementDepth, []);
					}

					elementsByDepth.get(elementDepth).push({ el, Decorator });
				});
			});

			return elementsByDepth;
		};

		const elementsByDepth = collectElementsByDepth(rootElement);
		const sortedDepths = Array.from(elementsByDepth.keys())
			.sort((a, b) => a - b)
			.map((depth) => elementsByDepth.get(depth));

		/**
		 * Process decorators level by level, ancestors first
		 */
		const processDecoratorsSequentially = async () =>
			arrayPromiseChain(sortedDepths, (depthElements) =>
				Promise.all(
					depthElements.map(({ el, Decorator }) => {
						if (el[Decorator.name]) {
							el[Decorator.name].scheduleUpdate();
						} else {
							el[Decorator.name] = new Decorator({ el });
						}
						return promisify(el[Decorator.name], DecoratorEvents.updated);
					}),
				),
			);

		return processDecoratorsSequentially().catch((/** @type {Error} */ error) => {
			this.logger.error('Error with Decorators', { error });
			return Promise.reject(error);
		});
	}

	/**
	 * Waits for the specified number of milliseconds and returns a Promise that resolves when the wait is complete.
	 * If no msToWait is provided, the function use requestAnimationFrame to wait for the next frame.
	 *
	 * @param {number} [msToWait] - The number of milliseconds to wait.
	 * @returns {Promise<void>} A Promise that resolves when the wait is complete.
	 */
	wait(msToWait) {
		this.logger.log(`[${this.wait.name}]`, { msToWait });

		return wait(msToWait);
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]<${BaseView.name}>`);

		this.$el.clearQueue().stop();
		this.cleanUpVideo();
		this.$el.empty();
		this.undelegateEvents();
		this.off();
	}
}
